import React from 'react';
import { formatCurrency } from '../../../shared/utils';
import type { Producer } from '../../../shared/types';
import { Pagination } from '../../../shared/components/Pagination';


const formatGrainStock = (value: number): string => {
  if (value === 0) return '0';
  if (value >= 1000000) {
    const millions = value / 1000000;
    return (millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(1)) + ' M';
  }
  if (value >= 1000) {
    const thousands = value / 1000;
    return (thousands % 1 === 0 ? thousands.toFixed(0) : thousands.toFixed(1)) + ' K';
  }
  return value % 1 === 0 ? value.toFixed(0) : value.toFixed(1);
};

interface ProducersTableProps {
  producers: Producer[];
  isLoading: boolean;
  currentPage: number;
  totalPages: number;
  onEdit: (producer: Producer) => void;
  onPageChange: (page: number) => void;
}

export const ProducersTable: React.FC<ProducersTableProps> = ({
  producers,
  isLoading,
  currentPage,
  totalPages,
  onEdit,
  onPageChange,
}) => (
  <div className="rounded-lg border overflow-hidden bg-white shadow-sm">
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-muted/50 border-b border-border">
            <th className="text-left px-2.5 py-4 font-semibold text-[#212121] whitespace-nowrap">Productor</th>
            <th className="text-left px-2.5 py-4 font-semibold text-[#212121] whitespace-nowrap">CUIT</th>
            <th className="text-right px-2.5 py-4 font-semibold text-[#212121] whitespace-nowrap">Saldo Actual</th>
            <th className="text-right px-2.5 py-4 font-semibold text-[#212121]">Límite<br />Descubierto</th>
            <th className="text-right px-2.5 py-4 font-semibold text-[#212121] whitespace-nowrap">Soja (tn)</th>
            <th className="text-right px-2.5 py-4 font-semibold text-[#212121] whitespace-nowrap">Maíz (tn)</th>
            <th className="text-right px-2.5 py-4 font-semibold text-[#212121] whitespace-nowrap">Trigo (tn)</th>
            <th className="text-right px-2.5 py-4 font-semibold text-[#212121] whitespace-nowrap">Sorgo (tn)</th>
            <th className="text-right px-2.5 py-4 font-semibold text-[#212121] whitespace-nowrap">Girasol (tn)</th>
            <th className="text-right px-2.5 py-4 font-semibold text-[#212121] whitespace-nowrap">Maní (tn)</th>
            <th className="text-center px-2.5 py-4 font-semibold text-[#212121]">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {producers.map(p => (
            <tr key={p.id} className="border-b hover:bg-muted/30">
              <td className="px-2.5 py-4 align-middle">
                <div className="font-semibold text-[#212121] whitespace-nowrap mb-1">{p.name}</div>
                <span className={`inline-flex px-2 py-0.5 text-xs rounded-full font-bold whitespace-nowrap ${
                  p.status === 'active'
                    ? 'bg-[#5BBA47]/15 text-[#006837]'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {p.status === 'active' ? 'Activo' : 'Inactivo'}
                </span>
              </td>
              <td className="px-2.5 py-4 text-muted-foreground whitespace-nowrap align-middle">{p.cuit}</td>
              <td className="px-2.5 py-4 text-right font-medium text-[#212121] whitespace-nowrap align-middle">{formatCurrency(p.balance)}</td>
              <td className="px-2.5 py-4 text-right text-[#212121] whitespace-nowrap align-middle">{formatCurrency(p.overdraft)}</td>
              <td className="px-2.5 py-4 text-right text-[#212121] whitespace-nowrap align-middle">{formatGrainStock(p.soja)}</td>
              <td className="px-2.5 py-4 text-right text-[#212121] whitespace-nowrap align-middle">{formatGrainStock(p.maiz)}</td>
              <td className="px-2.5 py-4 text-right text-[#212121] whitespace-nowrap align-middle">{formatGrainStock(p.trigo)}</td>
              <td className="px-2.5 py-4 text-right text-[#212121] whitespace-nowrap align-middle">{formatGrainStock(p.sorgo)}</td>
              <td className="px-2.5 py-4 text-right text-[#212121] whitespace-nowrap align-middle">{formatGrainStock(p.girasol)}</td>
              <td className="px-2.5 py-4 text-right text-[#212121] whitespace-nowrap align-middle">{formatGrainStock(p.mani)}</td>
              <td className="px-2.5 py-4 text-center align-middle">
                <button
                  onClick={() => onEdit(p)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-border rounded-md hover:bg-muted transition text-[#212121] font-semibold"
                  title="Editar productor"
                >
                  <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                  </svg>
                  Editar
                </button>
              </td>
            </tr>
          ))}
          {producers.length === 0 && !isLoading && (
            <tr>
              <td colSpan={11} className="px-2.5 py-8 text-center text-muted-foreground font-semibold">
                No se encontraron productores
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>

    {/* References Legend */}
    <div className="px-4 py-2 border-t border-border bg-muted/10 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
      <span className="font-semibold text-[#212121]">Referencias de volumen:</span>
      <span className="flex items-center gap-1"><span className="font-bold text-[#212121]">K:</span> miles de toneladas (x1.000 tn)</span>
      <span className="flex items-center gap-1"><span className="font-bold text-[#212121]">M:</span> millones de toneladas (x1.000.000 tn)</span>
    </div>

    {/* Pagination */}
    <Pagination 
      currentPage={currentPage} 
      totalPages={totalPages} 
      onPageChange={onPageChange} 
    />
  </div>
);
